import * as React from "react";
import { useSelector } from "react-redux";
import Loadable from "react-loadable";
import { Redirect, Route, RouteComponentProps } from "react-router-dom";
import styled from "styled-components";
import { Helmet } from "react-helmet";

import { ROUTES } from "@config/app.routing";

import { AVAILABLE_COUNTRIES, SERVICE_TYPE } from "@typings/globals";
import { LOADING_STATE } from "@typings/reduxThunkActions";

import { FadeInContainer, Section, OptionPanel } from "@ui/Atoms";
import { Column, Container, Row } from "@ui/Atoms/Grid";
import { StepProgress, ImpersonatorNotification } from "@ui/Molecules";

import { getQueryValue } from "@services/QueryParamsParser";

import { useAuth } from "@hooks/useAuth";
import { useTranslate } from "@hooks/useTranslate";
import { useBreakpoint } from "@hooks/useBreakpoint";
import { useLocalisation } from "@hooks/useLocalisation";
import {
  FEATURE_TOGGLE_ORIGIN,
  useFeatureToggle,
  useFeatureToggleManager,
} from "@hooks/useFeatureToggle";

import { EndOfTenancyPriceUpdater } from "@app/containers";
import {
  leaveProcess,
  setServiceLocation,
} from "@app/redux/endOfTenancy/endOfTenancy.actions";
import { getQueryParams } from "@app/redux/endOfTenancy/endOfTenancy.selectors";
import { ServiceTypeAvailabilityChecker } from "@app/containers/InactiveServiceTypeChecker";
import {
  resetState as resetServiceLocationState,
  fetchServiceLocationCoverage,
} from "@app/redux/serviceLocation/serviceLocation.actions";
import { useAppDispatch } from "@app/redux/hooks";

import { EndOfTenancyModals } from "./modals/EndOfTenancy.modals";
import { SidebarSkeleton, PageSkeleton } from "./EndOfTenancy.skeletons";
import { EndOfTenancySummary } from "./Sidebar";

const routeToStepMap: { [k: string]: number } = {
  [ROUTES.END_OF_TENANCY]: 2,
  [ROUTES.END_OF_TENANCY_CHECKOUT]: 3,
};

const LoadableCleaningInformation = Loadable({
  loader: () => import("./EndOfTenancyProcess"),
  render(loaded: any, props: any): JSX.Element {
    const Component = loaded.CleaningInformation;
    return <Component {...props} />;
  },
  loading: PageSkeleton,
});

const LoadableCheckout = Loadable({
  loader: () => import("./EndOfTenancyProcess"),
  render(loaded: any, props: any): JSX.Element {
    const Component = loaded.Checkout;
    return <Component {...props} />;
  },
  loading: PageSkeleton,
});

const LoadableSidebar = Loadable({
  loader: () => import("./EndOfTenancy.sidebar"),
  render(loaded: any, props: any): JSX.Element {
    const Component = loaded.EndOfTenancySidebar;
    return <Component {...props} />;
  },
  loading: SidebarSkeleton,
});

interface Props extends RouteComponentProps {
  currentUrl: string;
}

const EndOfTenancy = (props: Props): React.ReactElement | null => {
  const dispatch = useAppDispatch();
  const translate = useTranslate("batmaid_pwa_end_of_tenancy");
  const { isUserAdmin, isAdminImpersonator } = useAuth();
  const { country, language } = useLocalisation();
  const { desktop, desktopLarge } = useBreakpoint("v1");

  const isFullyBooked = useFeatureToggle("pwa_eot_fully_booked_modal");

  const queryParams = useSelector(getQueryParams);

  const renderSidebar = desktop || desktopLarge;

  const zip = getQueryValue(props.location.search, "postalCode");
  const isFirstPage = props.history.location.pathname === ROUTES.END_OF_TENANCY;

  const { origin: featureToggleOrigin, status: featureToggleStatus } =
    useFeatureToggleManager();
  const featureTogglesAreReady =
    featureToggleOrigin !== FEATURE_TOGGLE_ORIGIN.DEFAULT ||
    [LOADING_STATE.SUCCEEDED, LOADING_STATE.FAILED].includes(
      featureToggleStatus,
    );

  React.useEffect(() => {
    (async () => {
      if (zip) {
        const { onSuccess } = await dispatch(
          fetchServiceLocationCoverage(zip, undefined, { saveData: true }),
        );

        onSuccess(payload => {
          dispatch(setServiceLocation(payload.data.serviceLocationId!));
        });
      }
    })();
  }, [zip, dispatch]);

  React.useEffect(() => {
    return () => {
      dispatch(leaveProcess());
      dispatch(resetServiceLocationState());
    };
  }, []);

  const handleInvalidPostalCodeQuery = (): void => {
    window.location.href = `/${language}`;
  };

  // redirect before any rendering logic begins
  if (!zip && isFirstPage) {
    handleInvalidPostalCodeQuery();
    return null;
  }

  const summary = {
    isDesktop: renderSidebar,
  };

  if (isUserAdmin && !isAdminImpersonator) {
    return <ImpersonatorNotification />;
  }

  if (isFullyBooked) {
    window.location.href = `/${language}`;
    return null;
  }

  return (
    <StyledContainer>
      <ServiceTypeAvailabilityChecker
        serviceType={SERVICE_TYPE.END_OF_TENANCY}
        postalCodeQuery={String(zip)}
      />
      {country === AVAILABLE_COUNTRIES.CH && (
        <Helmet>
          <title>{translate("meta_title")}</title>
        </Helmet>
      )}
      <EndOfTenancyPriceUpdater />
      <EndOfTenancyModals />
      {!renderSidebar && (
        <EndOfTenancySummary isDesktop={summary.isDesktop} withHeader={false} />
      )}
      <Row>
        <Column xs={12} lg={8}>
          <Section flat smallMargins>
            <OptionPanel isThin>
              <StepProgress
                activeStep={routeToStepMap[props.location.pathname]}
                steps={[
                  {
                    label: translate("step_postal_code"),
                  },
                  {
                    label: translate("step_cleaning_information"),
                    route: `${ROUTES.END_OF_TENANCY}${queryParams}`,
                    allowDirectNavigation: true,
                  },
                  {
                    label: translate("step_check_out"),
                    route: ROUTES.END_OF_TENANCY_CHECKOUT,
                  },
                ]}
              />
            </OptionPanel>
          </Section>
          <Section small noBottomMargin>
            <Route
              exact
              path={ROUTES.END_OF_TENANCY}
              render={() => (
                <FadeInContainer>
                  {featureTogglesAreReady && <LoadableCleaningInformation />}
                  {!featureTogglesAreReady && <PageSkeleton />}
                </FadeInContainer>
              )}
            />
            <Route
              exact
              path={ROUTES.END_OF_TENANCY_CHECKOUT}
              render={() => (
                <FadeInContainer>
                  {featureTogglesAreReady && <LoadableCheckout />}
                  {!featureTogglesAreReady && <PageSkeleton />}
                </FadeInContainer>
              )}
            />
          </Section>
        </Column>
        {renderSidebar && (
          <Column lg={4}>
            {featureTogglesAreReady && <LoadableSidebar {...summary} />}
            {!featureTogglesAreReady && <PageSkeleton />}
          </Column>
        )}
      </Row>
    </StyledContainer>
  );
};

export { EndOfTenancy };

const StyledContainer = styled(Container)`
  display: flex;
  flex-direction: column;

  ${props => props.theme.breakpoints.upToTablet} {
    padding-left: ${props => props.theme.spacing.spacing_24};
    padding-right: ${props => props.theme.spacing.spacing_24};
    padding-top: ${props => props.theme.spacing.spacing_16};
  }

  ${props => props.theme.breakpoints.desktop} {
    padding-top: ${props => props.theme.margins.base_x4};
    padding-bottom: ${props => props.theme.margins.base_x4};
    display: block;
  }

  > ${Row} {
    ${props => props.theme.breakpoints.upToDesktop} {
      margin-left: 0;
      margin-right: 0;
    }
  }
`;
